import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, Subject, of, throwError} from 'rxjs';
import {catchError, map, shareReplay, startWith, switchMap, tap} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {environment} from '../../environments/environment';
import {ProjectType, ProjectTypeListResponse,} from '../../models/project-analysis';
import {ToastService} from '../toast/toast.service';

@Injectable({
    providedIn: 'root'
})
export class ProjectTypeService {
    private readonly httpClient = inject(HttpClient);
    private readonly toastService = inject(ToastService);
    private readonly translateService = inject(TranslateService);
    private readonly baseUrl = `${environment.apiUrl}/project-types`;

    private readonly reload$ = new Subject<void>();

    /**
     * All project types, fetched once and shared between subscribers.
     * Mutations trigger a reload; subscribers keep receiving the fresh list.
     * NOTE: this stream never completes — one-shot consumers (forkJoin,
     * firstValueFrom, modals) must pipe take(1).
     */
    public readonly projectTypes$: Observable<ProjectType[]> = this.reload$.pipe(
        startWith(undefined as void),
        switchMap(() => this.httpClient.get<ProjectTypeListResponse>(this.baseUrl).pipe(
            catchError((error) => {
                this.notifyError(error, 'ERRORS.project_types_not_retrieved');
                return of([] as ProjectType[]);
            })
        )),
        shareReplay({bufferSize: 1, refCount: false})
    );

    public list(): Observable<ProjectType[]> {
        return this.projectTypes$;
    }

    public reload(): void {
        this.reload$.next();
    }

    public getById(projectTypeId: number): Observable<ProjectType | undefined> {
        return this.list().pipe(
            map((projectTypes) => projectTypes.find((projectType) => projectType.id === projectTypeId))
        );
    }

    /**
     * Adds a new project type.
     */
    public add(data: ProjectType): Observable<ProjectType> {
        return this.httpClient.post<ProjectType>(this.baseUrl, data).pipe(
            tap(() => this.reload()),
            catchError((error) => this.handleError(error, 'ERRORS.project_type_not_added'))
        );
    }

    /**
     * Updates specified project type.
     */
    public update(projectTypeId: number, data: ProjectType): Observable<ProjectType> {
        return this.httpClient.put<ProjectType>(`${this.baseUrl}/${projectTypeId}`, data).pipe(
            tap(() => this.reload()),
            catchError((error) => this.handleError(error, 'ERRORS.project_type_not_updated'))
        );
    }

    /**
     * Deletes specified project type.
     */
    public delete(projectTypeId: number): Observable<void> {
        return this.httpClient.delete<void>(`${this.baseUrl}/${projectTypeId}`).pipe(
            tap(() => this.reload()),
            catchError((error) => this.handleError(error, 'ERRORS.project_type_not_deleted'))
        );
    }

    private handleError(error: { status?: number }, translationKey: string): Observable<never> {
        this.notifyError(error, translationKey);

        return throwError(() => error);
    }

    private notifyError(error: { status?: number }, translationKey: string): void {
        this.toastService.openError(error.status ?? 0, this.translateService.instant(translationKey));
    }
}
